// Quiz Data and Archetype Definitions

export const BECOMING_AXES = [
  "Freedom↔Stability",
  "Growth↔Contentment",
  "Authenticity↔Harmony",
  "Impact↔Inner peace",
  "Connection↔Independence",
  "Expression↔Mastery",
  "Simplicity↔Pleasure"
];

export const ITEMS_PER_DIM = {
  "Freedom↔Stability": 6,
  "Growth↔Contentment": 6,
  "Authenticity↔Harmony": 6,
  "Impact↔Inner peace": 6,
  "Connection↔Independence": 6,
  "Expression↔Mastery": 6,
  "Simplicity↔Pleasure": 6
};

// Educational explanations for each dimension
export const DIMENSION_WHY = {
  "Freedom↔Stability": "This dimension reveals how you balance autonomy with security. Freedom-oriented people thrive on spontaneity and independence, while stability-seekers find comfort in predictability and structure. Both are valid—understanding your position helps you design a life that truly supports you.",
  "Growth↔Contentment": "This reflects your relationship with ambition and satisfaction. Growth-focused individuals are energized by constant improvement, while contentment-oriented people find peace in appreciation. Knowing where you stand helps you set goals that align with your authentic desires, not external pressures.",
  "Authenticity↔Harmony": "This measures how you navigate self-expression versus social cohesion. Authenticity-driven people prioritize being genuine even if it creates friction, while harmony-seekers value smooth relationships and adaptation. Understanding this helps you honor your truth while relating skillfully to others.",
  "Impact↔Inner peace": "This dimension shows whether you're energized by external change or internal calm. Impact-oriented people find meaning in influence and achievement, while inner peace seekers prioritize mental clarity and presence. Recognizing your tendency helps you find sustainable meaning.",
  "Connection↔Independence": "This reveals how you balance intimacy with autonomy. Connection-oriented people thrive on deep relationships and shared experiences, while independence-focused individuals need solitude and self-reliance. Understanding this helps you build relationships that energize rather than drain you.",
  "Expression↔Mastery": "This dimension captures how you engage with your skills and creativity. Expression-focused people prioritize authentic self-communication, while mastery-oriented individuals focus on refinement and expertise. Knowing your tendency helps you pursue projects that truly fulfill you.",
  "Simplicity↔Pleasure": "This reflects how you relate to life's richness and complexity. Simplicity-seekers find peace in minimalism and essentialism, while pleasure-oriented people savor sensory experiences and abundance. Understanding this helps you create an environment where you feel truly at home."
};

// Archetypes with their characteristics
export const ARCHETYPES = {
  "The Pioneer": {
    name: "The Pioneer",
    mantra: "I chart my own course and embrace the unknown",
    colorHex: "#FF6B6B",
    growthEdge: "Learning to appreciate moments of stability and connection without losing your adventurous spirit.",
    dominantPoles: ["Freedom", "Growth", "Independence"]
  },
  "The Builder": {
    name: "The Builder",
    mantra: "I create lasting value through steady progress",
    colorHex: "#4ECDC4",
    growthEdge: "Allowing space for spontaneity and authentic expression alongside your structured approach.",
    dominantPoles: ["Stability", "Mastery", "Impact"]
  },
  "The Harmonizer": {
    name: "The Harmonizer",
    mantra: "I bring people together and create beautiful connections",
    colorHex: "#4ECAA0",
    growthEdge: "Finding your authentic voice even when it might disrupt harmony.",
    dominantPoles: ["Harmony", "Connection", "Inner peace"]
  },
  "The Seeker": {
    name: "The Seeker",
    mantra: "I pursue wisdom and continuous transformation",
    colorHex: "#F38181",
    growthEdge: "Balancing your quest for growth with moments of contentment and presence.",
    dominantPoles: ["Growth", "Authenticity", "Freedom"]
  },
  "The Creator": {
    name: "The Creator",
    mantra: "I express my unique vision and bring ideas to life",
    colorHex: "#AA96DA",
    growthEdge: "Developing mastery without losing the joy of spontaneous expression.",
    dominantPoles: ["Expression", "Freedom", "Authenticity"]
  },
  "The Guardian": {
    name: "The Guardian",
    mantra: "I protect what matters and create safe spaces for growth",
    colorHex: "#E88FB5",
    growthEdge: "Allowing others their independence while maintaining your supportive presence.",
    dominantPoles: ["Stability", "Connection", "Inner peace"]
  },
  "The Visionary": {
    name: "The Visionary",
    mantra: "I see possibilities and inspire transformation",
    colorHex: "#F4A300",
    growthEdge: "Grounding your vision in practical mastery and sustainable action.",
    dominantPoles: ["Impact", "Growth", "Expression"]
  },
  "The Sage": {
    name: "The Sage",
    mantra: "I cultivate inner peace and share wisdom thoughtfully",
    colorHex: "#5BA4CF",
    growthEdge: "Stepping into impact and connection without sacrificing your peace.",
    dominantPoles: ["Inner peace", "Contentment", "Mastery"]
  },
  "The Catalyst": {
    name: "The Catalyst",
    mantra: "I ignite change and challenge the status quo",
    colorHex: "#FF7B7E",
    growthEdge: "Balancing your drive for impact with inner peace and harmony.",
    dominantPoles: ["Impact", "Authenticity", "Freedom"]
  },
  "The Connoisseur": {
    name: "The Connoisseur",
    mantra: "I savor life's richness and cultivate refined experiences",
    colorHex: "#E896B0",
    growthEdge: "Finding joy in simplicity alongside your appreciation for pleasure.",
    dominantPoles: ["Pleasure", "Mastery", "Contentment"]
  }
};

// Quiz Questions - 42 total (6 per dimension)
export const QUIZ_QUESTIONS = [
  // Freedom↔Stability (6 questions)
  {
    id: 1,
    dimension: "Freedom↔Stability",
    question: "How do you prefer to structure your week?",
    subtitle: "Think about what energizes you most",
    options: [
      { text: "I love having flexibility to decide each day", value: -2, pole: "Freedom" },
      { text: "I prefer some structure with room for spontaneity", value: -1, pole: "Freedom" },
      { text: "I like a balanced mix of routine and flexibility", value: 0, pole: "Balanced" },
      { text: "I feel best with consistent routines", value: 1, pole: "Stability" },
      { text: "I thrive on predictable schedules and planning", value: 2, pole: "Stability" }
    ]
  },
  {
    id: 2,
    dimension: "Freedom↔Stability",
    question: "When making life decisions, what guides you?",
    subtitle: "Consider your recent choices",
    options: [
      { text: "Following my intuition and what feels right now", value: -2, pole: "Freedom" },
      { text: "Balancing intuition with some practical considerations", value: -1, pole: "Freedom" },
      { text: "Weighing freedom and security equally", value: 0, pole: "Balanced" },
      { text: "Considering long-term security first", value: 1, pole: "Stability" },
      { text: "Prioritizing safety and predictable outcomes", value: 2, pole: "Stability" }
    ]
  },
  {
    id: 3,
    dimension: "Freedom↔Stability",
    question: "Your ideal living situation is:",
    subtitle: "What would make you happiest?",
    options: [
      { text: "Able to move freely, minimal commitments", value: -2, pole: "Freedom" },
      { text: "Flexible with some roots in one place", value: -1, pole: "Freedom" },
      { text: "A stable base with travel opportunities", value: 0, pole: "Balanced" },
      { text: "Established roots with familiar surroundings", value: 1, pole: "Stability" },
      { text: "Deep roots, owned home, settled community", value: 2, pole: "Stability" }
    ]
  },
  {
    id: 4,
    dimension: "Freedom↔Stability",
    question: "How do you feel about long-term commitments?",
    subtitle: "Be honest with yourself",
    options: [
      { text: "They feel restrictive and limiting", value: -2, pole: "Freedom" },
      { text: "I'm cautious but open to the right ones", value: -1, pole: "Freedom" },
      { text: "Depends on what it is", value: 0, pole: "Balanced" },
      { text: "I appreciate the security they provide", value: 1, pole: "Stability" },
      { text: "I actively seek and value them", value: 2, pole: "Stability" }
    ]
  },
  {
    id: 5,
    dimension: "Freedom↔Stability",
    question: "When traveling, you prefer to:",
    subtitle: "Think about your last trip",
    options: [
      { text: "Have zero plans, pure adventure", value: -2, pole: "Freedom" },
      { text: "Rough outline with lots of flexibility", value: -1, pole: "Freedom" },
      { text: "Mix of planned activities and free time", value: 0, pole: "Balanced" },
      { text: "Detailed itinerary with some flexibility", value: 1, pole: "Stability" },
      { text: "Everything planned and booked in advance", value: 2, pole: "Stability" }
    ]
  },
  {
    id: 6,
    dimension: "Freedom↔Stability",
    question: "Your relationship with savings and financial planning:",
    subtitle: "What feels most true?",
    options: [
      { text: "I live in the moment, money comes and goes", value: -2, pole: "Freedom" },
      { text: "I save when I can but don't stress about it", value: -1, pole: "Freedom" },
      { text: "I balance enjoying now and saving for later", value: 0, pole: "Balanced" },
      { text: "I prioritize consistent saving and planning", value: 1, pole: "Stability" },
      { text: "I have detailed financial plans and emergency funds", value: 2, pole: "Stability" }
    ]
  },

  // Growth↔Contentment (6 questions)
  {
    id: 7,
    dimension: "Growth↔Contentment",
    question: "How do you feel about your current life situation?",
    subtitle: "Check in with yourself right now",
    options: [
      { text: "Restless, I need to evolve and expand constantly", value: -2, pole: "Growth" },
      { text: "Satisfied but always looking for next level", value: -1, pole: "Growth" },
      { text: "Grateful and still motivated to improve", value: 0, pole: "Balanced" },
      { text: "Mostly content with small improvements", value: 1, pole: "Contentment" },
      { text: "Deeply grateful, no need to change much", value: 2, pole: "Contentment" }
    ]
  },
  {
    id: 8,
    dimension: "Growth↔Contentment",
    question: "When you achieve a goal, you typically:",
    subtitle: "Reflect on recent accomplishments",
    options: [
      { text: "Immediately set a bigger goal", value: -2, pole: "Growth" },
      { text: "Celebrate briefly then look ahead", value: -1, pole: "Growth" },
      { text: "Enjoy the moment then plan next steps", value: 0, pole: "Balanced" },
      { text: "Savor the achievement for a while", value: 1, pole: "Contentment" },
      { text: "Rest in the accomplishment fully", value: 2, pole: "Contentment" }
    ]
  },
  {
    id: 9,
    dimension: "Growth↔Contentment",
    question: "Your approach to learning new things:",
    subtitle: "Think about the past few months",
    options: [
      { text: "Always taking courses, reading, pushing limits", value: -2, pole: "Growth" },
      { text: "Regularly seeking new knowledge and skills", value: -1, pole: "Growth" },
      { text: "Learn when interested or needed", value: 0, pole: "Balanced" },
      { text: "Content with current knowledge, learn casually", value: 1, pole: "Contentment" },
      { text: "Happy with what I know, rarely seek new learning", value: 2, pole: "Contentment" }
    ]
  },
  {
    id: 10,
    dimension: "Growth↔Contentment",
    question: "How often do you compare yourself to others?",
    subtitle: "Be honest about your inner dialogue",
    options: [
      { text: "Constantly, it motivates me to do more", value: -2, pole: "Growth" },
      { text: "Often, I use it as inspiration", value: -1, pole: "Growth" },
      { text: "Sometimes, with mixed feelings", value: 0, pole: "Balanced" },
      { text: "Rarely, I focus on my own path", value: 1, pole: "Contentment" },
      { text: "Never, I'm content with who I am", value: 2, pole: "Contentment" }
    ]
  },
  {
    id: 11,
    dimension: "Growth↔Contentment",
    question: "Your relationship with productivity and optimization:",
    subtitle: "What resonates most?",
    options: [
      { text: "Always optimizing, tracking, improving systems", value: -2, pole: "Growth" },
      { text: "Regularly looking for ways to be more effective", value: -1, pole: "Growth" },
      { text: "Productive when needed, relaxed otherwise", value: 0, pole: "Balanced" },
      { text: "Do what needs doing without overthinking", value: 1, pole: "Contentment" },
      { text: "Flow through tasks naturally, no pressure", value: 2, pole: "Contentment" }
    ]
  },
  {
    id: 12,
    dimension: "Growth↔Contentment",
    question: "When you look at your future self:",
    subtitle: "Imagine yourself in 5 years",
    options: [
      { text: "I see major transformations and achievements", value: -2, pole: "Growth" },
      { text: "I envision clear progress and evolution", value: -1, pole: "Growth" },
      { text: "I see both growth and stability", value: 0, pole: "Balanced" },
      { text: "I hope for similar life with minor improvements", value: 1, pole: "Contentment" },
      { text: "I'm happy if things stay mostly the same", value: 2, pole: "Contentment" }
    ]
  },

  // Authenticity↔Harmony (6 questions)
  {
    id: 13,
    dimension: "Authenticity↔Harmony",
    question: "In group settings, you tend to:",
    subtitle: "Think about recent social situations",
    options: [
      { text: "Always express my true opinion, even if unpopular", value: -2, pole: "Authenticity" },
      { text: "Share my views while being respectful", value: -1, pole: "Authenticity" },
      { text: "Balance honesty with group dynamics", value: 0, pole: "Balanced" },
      { text: "Adjust my opinions to maintain peace", value: 1, pole: "Harmony" },
      { text: "Prioritize group harmony over my personal views", value: 2, pole: "Harmony" }
    ]
  },
  {
    id: 14,
    dimension: "Authenticity↔Harmony",
    question: "When someone asks 'How are you?' you:",
    subtitle: "What comes naturally?",
    options: [
      { text: "Tell them exactly how I'm really feeling", value: -2, pole: "Authenticity" },
      { text: "Share authentically if I trust them", value: -1, pole: "Authenticity" },
      { text: "Depends on context and relationship", value: 0, pole: "Balanced" },
      { text: "Give a polite, positive response usually", value: 1, pole: "Harmony" },
      { text: "Always say 'fine' or 'good' to keep it smooth", value: 2, pole: "Harmony" }
    ]
  },
  {
    id: 15,
    dimension: "Authenticity↔Harmony",
    question: "Your approach to conflict:",
    subtitle: "When tensions arise",
    options: [
      { text: "Address issues directly and immediately", value: -2, pole: "Authenticity" },
      { text: "Speak up about important disagreements", value: -1, pole: "Authenticity" },
      { text: "Address when necessary, avoid when minor", value: 0, pole: "Balanced" },
      { text: "Try to smooth things over diplomatically", value: 1, pole: "Harmony" },
      { text: "Avoid conflict at all costs, keep the peace", value: 2, pole: "Harmony" }
    ]
  },
  {
    id: 16,
    dimension: "Authenticity↔Harmony",
    question: "How do you present yourself on social media?",
    subtitle: "If you use social media",
    options: [
      { text: "Raw, unfiltered, this is exactly who I am", value: -2, pole: "Authenticity" },
      { text: "Mostly authentic with some curation", value: -1, pole: "Authenticity" },
      { text: "Authentic but mindful of audience", value: 0, pole: "Balanced" },
      { text: "Carefully curated to maintain relationships", value: 1, pole: "Harmony" },
      { text: "Only post what won't upset anyone", value: 2, pole: "Harmony" }
    ]
  },
  {
    id: 17,
    dimension: "Authenticity↔Harmony",
    question: "When you disagree with a friend's decision:",
    subtitle: "What's your instinct?",
    options: [
      { text: "Tell them directly what I really think", value: -2, pole: "Authenticity" },
      { text: "Share my concerns honestly but kindly", value: -1, pole: "Authenticity" },
      { text: "Speak up if serious, otherwise support them", value: 0, pole: "Balanced" },
      { text: "Ask questions but don't voice disagreement", value: 1, pole: "Harmony" },
      { text: "Support their choice to maintain friendship", value: 2, pole: "Harmony" }
    ]
  },
  {
    id: 18,
    dimension: "Authenticity↔Harmony",
    question: "Your relationship with 'white lies':",
    subtitle: "Small untruths to spare feelings",
    options: [
      { text: "I never lie, even to spare feelings", value: -2, pole: "Authenticity" },
      { text: "Rarely, only if truth causes unnecessary harm", value: -1, pole: "Authenticity" },
      { text: "Sometimes, depends on the situation", value: 0, pole: "Balanced" },
      { text: "Often use them to keep things pleasant", value: 1, pole: "Harmony" },
      { text: "Regularly, they help maintain harmony", value: 2, pole: "Harmony" }
    ]
  },

  // Impact↔Inner peace (6 questions)
  {
    id: 19,
    dimension: "Impact↔Inner peace",
    question: "What drives you most?",
    subtitle: "Deep down, what matters?",
    options: [
      { text: "Making a significant mark on the world", value: -2, pole: "Impact" },
      { text: "Contributing meaningfully to something bigger", value: -1, pole: "Impact" },
      { text: "Both external contribution and inner calm", value: 0, pole: "Balanced" },
      { text: "Finding peace and centeredness", value: 1, pole: "Inner peace" },
      { text: "Complete inner tranquility and presence", value: 2, pole: "Inner peace" }
    ]
  },
  {
    id: 20,
    dimension: "Impact↔Inner peace",
    question: "How do you measure a life well-lived?",
    subtitle: "What would make you feel fulfilled?",
    options: [
      { text: "By the change I created in the world", value: -2, pole: "Impact" },
      { text: "By the positive difference I made for others", value: -1, pole: "Impact" },
      { text: "By both impact and personal wellbeing", value: 0, pole: "Balanced" },
      { text: "By the peace and wisdom I cultivated", value: 1, pole: "Inner peace" },
      { text: "By how centered and present I remained", value: 2, pole: "Inner peace" }
    ]
  },
  {
    id: 21,
    dimension: "Impact↔Inner peace",
    question: "Your energy levels and boundaries:",
    subtitle: "How do you manage your energy?",
    options: [
      { text: "Push through exhaustion for important work", value: -2, pole: "Impact" },
      { text: "Work hard but take breaks when needed", value: -1, pole: "Impact" },
      { text: "Balance effort with self-care", value: 0, pole: "Balanced" },
      { text: "Protect my energy carefully", value: 1, pole: "Inner peace" },
      { text: "Prioritize rest and rejuvenation above all", value: 2, pole: "Inner peace" }
    ]
  },
  {
    id: 22,
    dimension: "Impact↔Inner peace",
    question: "When you see injustice or problems in the world:",
    subtitle: "What's your response?",
    options: [
      { text: "I must act and do something about it", value: -2, pole: "Impact" },
      { text: "I look for ways to contribute to solutions", value: -1, pole: "Impact" },
      { text: "I care but balance action with acceptance", value: 0, pole: "Balanced" },
      { text: "I accept what I cannot change", value: 1, pole: "Inner peace" },
      { text: "I focus on inner peace, not external problems", value: 2, pole: "Inner peace" }
    ]
  },
  {
    id: 23,
    dimension: "Impact↔Inner peace",
    question: "Your relationship with ambition:",
    subtitle: "How does ambition feel to you?",
    options: [
      { text: "Highly ambitious, driven to achieve big things", value: -2, pole: "Impact" },
      { text: "Ambitious in ways that matter to me", value: -1, pole: "Impact" },
      { text: "Moderate ambition balanced with contentment", value: 0, pole: "Balanced" },
      { text: "Low ambition, prefer simple goals", value: 1, pole: "Inner peace" },
      { text: "No ambition, fully present with what is", value: 2, pole: "Inner peace" }
    ]
  },
  {
    id: 24,
    dimension: "Impact↔Inner peace",
    question: "How do you spend your ideal free time?",
    subtitle: "When you have a free day",
    options: [
      { text: "Working on projects that create change", value: -2, pole: "Impact" },
      { text: "Learning or building something meaningful", value: -1, pole: "Impact" },
      { text: "Mix of productive and restful activities", value: 0, pole: "Balanced" },
      { text: "Quiet activities, reflection, light hobbies", value: 1, pole: "Inner peace" },
      { text: "Meditation, rest, complete stillness", value: 2, pole: "Inner peace" }
    ]
  },

  // Connection↔Independence (6 questions)
  {
    id: 25,
    dimension: "Connection↔Independence",
    question: "When facing a challenge, you prefer to:",
    subtitle: "Think about recent difficulties",
    options: [
      { text: "Immediately talk it through with others", value: -2, pole: "Connection" },
      { text: "Reach out to trusted people for perspective", value: -1, pole: "Connection" },
      { text: "Think alone first, then discuss if needed", value: 0, pole: "Balanced" },
      { text: "Work through it independently mostly", value: 1, pole: "Independence" },
      { text: "Always solve it completely on my own", value: 2, pole: "Independence" }
    ]
  },
  {
    id: 26,
    dimension: "Connection↔Independence",
    question: "Your ideal evening looks like:",
    subtitle: "After a typical day",
    options: [
      { text: "Surrounded by friends or loved ones", value: -2, pole: "Connection" },
      { text: "Quality time with someone close", value: -1, pole: "Connection" },
      { text: "Sometimes social, sometimes alone", value: 0, pole: "Balanced" },
      { text: "Solo activities or small interactions", value: 1, pole: "Independence" },
      { text: "Complete solitude to recharge", value: 2, pole: "Independence" }
    ]
  },
  {
    id: 27,
    dimension: "Connection↔Independence",
    question: "How important are other people's opinions of you?",
    subtitle: "Be truly honest",
    options: [
      { text: "Very important, I care deeply what others think", value: -2, pole: "Connection" },
      { text: "Important, especially from people I respect", value: -1, pole: "Connection" },
      { text: "Somewhat important but not defining", value: 0, pole: "Balanced" },
      { text: "Not very important, I trust myself", value: 1, pole: "Independence" },
      { text: "Completely irrelevant, I'm self-directed", value: 2, pole: "Independence" }
    ]
  },
  {
    id: 28,
    dimension: "Connection↔Independence",
    question: "Your communication style:",
    subtitle: "How do you stay in touch?",
    options: [
      { text: "Constant communication, text all day", value: -2, pole: "Connection" },
      { text: "Regular check-ins with close people", value: -1, pole: "Connection" },
      { text: "Respond when I can, initiate sometimes", value: 0, pole: "Balanced" },
      { text: "Respond when necessary, rarely initiate", value: 1, pole: "Independence" },
      { text: "Minimal contact, I need lots of space", value: 2, pole: "Independence" }
    ]
  },
  {
    id: 29,
    dimension: "Connection↔Independence",
    question: "When making important life decisions:",
    subtitle: "Who's involved in the process?",
    options: [
      { text: "Consult extensively with my circle", value: -2, pole: "Connection" },
      { text: "Get input from key people I trust", value: -1, pole: "Connection" },
      { text: "Consider others' input but decide myself", value: 0, pole: "Balanced" },
      { text: "Mostly decide alone, inform others after", value: 1, pole: "Independence" },
      { text: "Completely independent decision-making", value: 2, pole: "Independence" }
    ]
  },
  {
    id: 30,
    dimension: "Connection↔Independence",
    question: "How do you feel after extended alone time?",
    subtitle: "Several days without much socializing",
    options: [
      { text: "Lonely and eager to connect immediately", value: -2, pole: "Connection" },
      { text: "Missing people, ready for social time", value: -1, pole: "Connection" },
      { text: "Neutral, could go either way", value: 0, pole: "Balanced" },
      { text: "Refreshed and recharged", value: 1, pole: "Independence" },
      { text: "Blissful and wish it could continue", value: 2, pole: "Independence" }
    ]
  },

  // Expression↔Mastery (6 questions)
  {
    id: 31,
    dimension: "Expression↔Mastery",
    question: "When creating something, you prioritize:",
    subtitle: "Whether art, work, or projects",
    options: [
      { text: "Pure creative expression, no rules", value: -2, pole: "Expression" },
      { text: "Authentic expression with some structure", value: -1, pole: "Expression" },
      { text: "Balance creativity with skill", value: 0, pole: "Balanced" },
      { text: "Technical excellence and refinement", value: 1, pole: "Mastery" },
      { text: "Perfection through deep mastery", value: 2, pole: "Mastery" }
    ]
  },
  {
    id: 32,
    dimension: "Expression↔Mastery",
    question: "Your approach to sharing your work:",
    subtitle: "Projects, ideas, creative output",
    options: [
      { text: "Share immediately, even rough drafts", value: -2, pole: "Expression" },
      { text: "Share early to get feedback and iterate", value: -1, pole: "Expression" },
      { text: "Share when it's reasonably polished", value: 0, pole: "Balanced" },
      { text: "Wait until it's refined and professional", value: 1, pole: "Mastery" },
      { text: "Only share when it's perfect", value: 2, pole: "Mastery" }
    ]
  },
  {
    id: 33,
    dimension: "Expression↔Mastery",
    question: "How do you learn new skills?",
    subtitle: "Your learning process",
    options: [
      { text: "Jump in and experiment wildly", value: -2, pole: "Expression" },
      { text: "Start creating with minimal instruction", value: -1, pole: "Expression" },
      { text: "Mix of experimentation and study", value: 0, pole: "Balanced" },
      { text: "Study fundamentals then practice systematically", value: 1, pole: "Mastery" },
      { text: "Master theory completely before attempting", value: 2, pole: "Mastery" }
    ]
  },
  {
    id: 34,
    dimension: "Expression↔Mastery",
    question: "Your relationship with 'good enough':",
    subtitle: "When is something done?",
    options: [
      { text: "'Done' is when I've expressed what I wanted", value: -2, pole: "Expression" },
      { text: "Done when it captures my vision, even if rough", value: -1, pole: "Expression" },
      { text: "Done when it meets the goal appropriately", value: 0, pole: "Balanced" },
      { text: "Done when it's polished and professional", value: 1, pole: "Mastery" },
      { text: "It's never truly done, always refineable", value: 2, pole: "Mastery" }
    ]
  },
  {
    id: 35,
    dimension: "Expression↔Mastery",
    question: "How do you feel about rules and conventions?",
    subtitle: "In your craft or work",
    options: [
      { text: "Rules are meant to be broken, I innovate freely", value: -2, pole: "Expression" },
      { text: "I bend rules to serve my creative vision", value: -1, pole: "Expression" },
      { text: "Follow them when useful, break when necessary", value: 0, pole: "Balanced" },
      { text: "Follow best practices and proven methods", value: 1, pole: "Mastery" },
      { text: "Master and honor all conventions deeply", value: 2, pole: "Mastery" }
    ]
  },
  {
    id: 36,
    dimension: "Expression↔Mastery",
    question: "When receiving feedback on your work:",
    subtitle: "How do you process criticism?",
    options: [
      { text: "I defend my creative choices, it's my expression", value: -2, pole: "Expression" },
      { text: "I listen but trust my creative intuition", value: -1, pole: "Expression" },
      { text: "I consider feedback thoughtfully", value: 0, pole: "Balanced" },
      { text: "I actively seek critique to improve", value: 1, pole: "Mastery" },
      { text: "I study all feedback intensely to perfect my craft", value: 2, pole: "Mastery" }
    ]
  },

  // Simplicity↔Pleasure (6 questions)
  {
    id: 37,
    dimension: "Simplicity↔Pleasure",
    question: "Your approach to material possessions:",
    subtitle: "What feels right to you?",
    options: [
      { text: "Extreme minimalism, almost nothing", value: -2, pole: "Simplicity" },
      { text: "Only essentials, very selective", value: -1, pole: "Simplicity" },
      { text: "Practical items plus some joyful things", value: 0, pole: "Balanced" },
      { text: "Quality items that bring pleasure", value: 1, pole: "Pleasure" },
      { text: "Surround myself with beautiful, luxurious things", value: 2, pole: "Pleasure" }
    ]
  },
  {
    id: 38,
    dimension: "Simplicity↔Pleasure",
    question: "When choosing a meal:",
    subtitle: "What guides your choice?",
    options: [
      { text: "Simple, nutritious, functional fuel", value: -2, pole: "Simplicity" },
      { text: "Healthy and satisfying, nothing fancy", value: -1, pole: "Simplicity" },
      { text: "Balance nutrition with enjoyment", value: 0, pole: "Balanced" },
      { text: "Delicious flavors and good experience", value: 1, pole: "Pleasure" },
      { text: "Exquisite taste and sensory delight", value: 2, pole: "Pleasure" }
    ]
  },
  {
    id: 39,
    dimension: "Simplicity↔Pleasure",
    question: "Your living space aesthetic:",
    subtitle: "Your ideal environment",
    options: [
      { text: "Bare walls, only absolute necessities", value: -2, pole: "Simplicity" },
      { text: "Clean, minimal, very uncluttered", value: -1, pole: "Simplicity" },
      { text: "Comfortable and personalized moderately", value: 0, pole: "Balanced" },
      { text: "Rich with comfort, art, and beauty", value: 1, pole: "Pleasure" },
      { text: "Opulent, layered, sensory feast", value: 2, pole: "Pleasure" }
    ]
  },
  {
    id: 40,
    dimension: "Simplicity↔Pleasure",
    question: "How do you spend discretionary money?",
    subtitle: "Money beyond necessities",
    options: [
      { text: "I save it, I don't need much", value: -2, pole: "Simplicity" },
      { text: "Very selective purchases, mostly save", value: -1, pole: "Simplicity" },
      { text: "Some treats, some savings", value: 0, pole: "Balanced" },
      { text: "Invest in experiences and quality items", value: 1, pole: "Pleasure" },
      { text: "Indulge in luxuries that bring joy", value: 2, pole: "Pleasure" }
    ]
  },
  {
    id: 41,
    dimension: "Simplicity↔Pleasure",
    question: "Your relationship with sensory experiences:",
    subtitle: "Food, music, textures, scents",
    options: [
      { text: "I barely notice them, not important", value: -2, pole: "Simplicity" },
      { text: "Appreciate them but don't seek them out", value: -1, pole: "Simplicity" },
      { text: "Enjoy them in moderation", value: 0, pole: "Balanced" },
      { text: "Actively seek beautiful sensory experiences", value: 1, pole: "Pleasure" },
      { text: "Central to my life, I'm a sensualist", value: 2, pole: "Pleasure" }
    ]
  },
  {
    id: 42,
    dimension: "Simplicity↔Pleasure",
    question: "When you have extra time, you prefer:",
    subtitle: "Unexpected free time",
    options: [
      { text: "Quiet simplicity, maybe a walk or rest", value: -2, pole: "Simplicity" },
      { text: "Simple pleasures like reading or nature", value: -1, pole: "Simplicity" },
      { text: "Mix of simple and indulgent activities", value: 0, pole: "Balanced" },
      { text: "Treat myself to something enjoyable", value: 1, pole: "Pleasure" },
      { text: "Indulge fully in luxury and comfort", value: 2, pole: "Pleasure" }
    ]
  }
];

// Calculate archetype from scores
export function calculateArchetype(scores) {
  // Find dominant poles (top 3 strongest tendencies)
  const sortedScores = Object.entries(scores)
    .map(([dim, score]) => {
      const parts = dim.split('↔');
      const pole = score > 0 ? parts[1] : (score < 0 ? parts[0] : 'Balanced');
      return { dimension: dim, pole, absScore: Math.abs(score) };
    })
    .sort((a, b) => b.absScore - a.absScore)
    .slice(0, 3);

  const dominantPoles = sortedScores.map(s => s.pole);

  // Match to archetype based on dominant poles
  for (const [key, archetype] of Object.entries(ARCHETYPES)) {
    const matches = archetype.dominantPoles.filter(pole => 
      dominantPoles.some(dp => dp.includes(pole))
    );
    if (matches.length >= 2) {
      return archetype;
    }
  }

  // Default to most balanced archetype if no clear match
  return ARCHETYPES["The Seeker"];
}

